import { type SearchContextType } from './global-search-utils'
import type { IconValues } from '@gat-ship/icons'
import type { GatRouter } from '@gat-ship/lib/router'
import type { StakeholderType, GatshipSearchOperator } from '@gat-ship/types'

export interface SearchResult<
  Metadata extends Record<string, any> = Record<string, any>
> {
  /** The id of the search result. */
  id: string
  /** The title of the search result. */
  title: string
  /** The type of context the search result belongs to */
  contextType: SearchContextType
  subTitle?: string
  icon?: IconValues
  /** The actions a user can perform on the search result. */
  actions: {
    /** The action to be performed when the search result is selected. */
    selected: (router: Omit<GatRouter, 'install'>) => void
  }
  /** The values that can be used to search for this result. If not provided, the title will be used. */
  searchValues?: string[]
  metadata?: Metadata
}
export interface RecentSearch {
  searchString: string
  context: SearchContextType
  timestamp: number
}

/** The type of the data that can be searched for.   */
export type GatshipSearchFieldType = 'string' | 'number' | 'boolean'

/*
| **Operator** | **Name**     | **Semantics**                            |
| ------------ | ------------ | ---------------------------------------- |
| `:`          | Default      | Type-dependent (Equals, Contains, etc.). |
| `:=`         | Equals       | Exact match.                             |
| `:~`         | Like         | Pattern match (starts-with/wildcard).    |
| `:*`         | Contains     | Word-based token match.                  |
| `:@`         | FullText     | Semantic/Vector search.                  |
| `:>`         | Greater Than | Numeric/Date only.                       |
| `:>=`        | GTE          | Greater than or Equal.                   |
| `:<`         | Less Than    | Numeric/Date only.                       |
| `:<=`        | LTE          | Less than or Equal.                      |
*/

export interface GatshipSearchOperatorMetadata {
  /** The name of the operator. */
  name: string
  /** The semantics of the operator. */
  semantics: string
  /** The value of the operator (without the colon prefix). */
  operatorValue: string
  /** The human readable value of the operator. Used in the UI. */
  humanReadableTitle: string
  /** The human readable description of the operator. Used in the UI. */
  humanReadableDescription: string
}

// Other parties = Building2
// Stakeholders = User
// Vendors = Handshake

export const gatshipSearchOperatorsMetadata: Record<
  GatshipSearchOperator,
  GatshipSearchOperatorMetadata
> = {
  equals: {
    name: 'Equals',
    operatorValue: '=',
    semantics: 'Exact match.',
    humanReadableTitle: 'Equals',
    humanReadableDescription: 'The field must exactly match the search term.'
  },
  contains: {
    name: 'Contains',
    operatorValue: '*',
    semantics: 'Word-based token match.',
    humanReadableTitle: 'Contains',
    humanReadableDescription: 'The value contains the search term.'
  },
  like: {
    name: 'Like',
    operatorValue: '~',
    semantics: 'Pattern match (starts-with/wildcard).',
    humanReadableTitle: 'Starts with',
    humanReadableDescription: 'The field must start with the search term.'
  }
  // fullText: {
  //   name: 'FullText',
  //   operatorValue: '@',
  //   semantics: 'Semantic/Vector search.',
  //   humanReadableTitle: 'FullText',
  //   humanReadableDescription: 'Semantic/Vector search.'
  // },
  // greaterThan: {
  //   name: 'Greater Than',
  //   operatorValue: '>',
  //   semantics: 'Numeric/Date only.',
  //   humanReadableTitle: 'Greater Than',
  //   humanReadableDescription: 'Greater Than.'
  // },
  // gte: {
  //   name: 'GTE',
  //   operatorValue: '>=',
  //   semantics: 'Greater than or Equal.',
  //   humanReadableTitle: 'GTE',
  //   humanReadableDescription: 'Greater than or Equal.'
  // },
  // lessThan: {
  //   name: 'Less Than',
  //   operatorValue: '<',
  //   semantics: 'Numeric/Date only.',
  //   humanReadableTitle: 'Less Than',
  //   humanReadableDescription: 'Less Than.'
  // },
  // lte: {
  //   name: 'LTE',
  //   operatorValue: '<=',
  //   semantics: 'Less than or Equal.',
  //   humanReadableTitle: 'LTE',
  //   humanReadableDescription: 'Less than or Equal.'
  // }
} as const

/** A field that can be searched for. */
export type GatshipSearchField<
  Field extends string,
  DataType extends GatshipSearchFieldType
> = {
  field: Field
  type: DataType
  operators: GatshipSearchOperator[]
}

export interface GatshipSearchFieldValue<
  Field extends string,
  DataType extends GatshipSearchFieldType
> extends Omit<GatshipSearchField<Field, DataType>, 'operators'> {
  value: DataType extends 'string' ? string : number
  operator: GatshipSearchOperator
}

export interface GatshipCustomSearchFieldValue<Field extends string, Value> {
  field: Field
  value: Value
}

type StakeholderNameField = GatshipSearchField<'name', 'string'>

type StakeholderTypeField = GatshipSearchField<'type', 'string'>

type StakeholderTypeFieldValue = GatshipCustomSearchFieldValue<
  'type',
  StakeholderType
>

export const stakeholderFields = {
  name: {
    field: 'name',
    type: 'string',
    operators: ['equals', 'contains']
  } satisfies StakeholderNameField,
  type: {
    field: 'type',
    type: 'string',
    operators: ['equals']
  } satisfies StakeholderTypeField
} as const

export type StakeholderFields = Partial<
  Record<keyof typeof stakeholderFields, typeof stakeholderFields>
>

export type StakeholderFieldValues = Partial<
  Record<
    keyof typeof stakeholderFields,
    GatshipSearchFieldValue<
      keyof typeof stakeholderFields,
      (typeof stakeholderFields)[keyof typeof stakeholderFields]['type']
    >
  >
>

export type StakeholderCustomFieldValues = Partial<
  Record<
    keyof typeof stakeholderFields,
    GatshipCustomSearchFieldValue<
      keyof typeof stakeholderFields,
      (typeof stakeholderFields)[keyof typeof stakeholderFields]['type']
    >
  >
>
const testFields: StakeholderFieldValues = {
  name: {
    field: 'name',
    operator: 'contains',
    type: 'string',
    value: 'aokd'
  },
  type: {
    field: 'type',
    operator: 'equals',
    type: 'string',
    value: 'vendor'
  }
}
